import { type ReactElement, type ReactNode, useId } from "react";
import {
  Box,
  FormControl,
  FormHelperText,
  InputAdornment,
  TextField,
  type TextFieldProps,
  Typography,
} from "@mui/material";

import {
  type Control,
  type FieldValues,
  type Path,
  useController,
} from "react-hook-form";

type TextInputProps<T extends FieldValues> = {
  fieldName: Path<T>;
  label?: ReactNode;
  hideLabel?: boolean;
  hideError?: boolean;
  hide?: boolean;
  step?: number;
  min?: number;
  max?: number;
  unit?: string;
  control: Control<T>;
} & TextFieldProps;

export const TextInput = <T extends FieldValues>({
  fieldName,
  label,
  hideLabel = false,
  hideError = false,
  fullWidth = true,
  control,
  hide,
  step,
  min,
  max,
  unit,
  ...inputProps
}: TextInputProps<T>): ReactElement | null => {
  const id = useId();

  const {
    field: { ...field },
    fieldState: { error },
  } = useController({
    name: fieldName,
    control,
  });

  if (hide) {
    return null;
  }

  return (
    <Box
      sx={{ display: "flex", flexDirection: "column", gap: 0.5, width: "100%" }}
    >
      {!hideLabel && <Typography id={id}>{label}</Typography>}
      <FormControl fullWidth={fullWidth} error={!!error} size={"small"}>
        <TextField
          size={"small"}
          fullWidth
          {...inputProps}
          {...field}
          slotProps={{
            input: {
              endAdornment: unit && (
                <InputAdornment position={"end"}>{unit}</InputAdornment>
              ),
            },
            htmlInput: {
              "aria-labelledby": hideLabel ? undefined : id,
              "aria-label": hideLabel ? label : undefined,
              step,
              min,
              max,
            },
          }}
          value={field.value ?? ""}
          error={!!error}
        />
        {!hideError && error?.message && (
          <FormHelperText>{error.message}</FormHelperText>
        )}
      </FormControl>
    </Box>
  );
};
